import React, { useState, useEffect } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { t } from '../i18n';
import PiPrice from '../components/PiPrice';

const API_BASE = '/api';

function flAuthHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
}

function authHeaders(token) {
  if (token) {
    return { Authorization: `Token ${token}`, 'Content-Type': 'application/json' };
  }
  try {
    return { ...flAuthHeaders(), 'Content-Type': 'application/json' };
  } catch (e) {
    return { 'Content-Type': 'application/json' };
  }
}

export default function Cart({ user }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkingOutId, setCheckingOutId] = useState(null);
  const [loadErr, setLoadErr] = useState('');

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/cart/`, { headers: authHeaders(user.token) });
      if (!res.ok) {
        setLoadErr('Cart load failed (' + res.status + '). Please log in again.');
        setItems([]);
        setLoading(false);
        return;
      }
      const data = await safeJson(res);
      setLoadErr('');
      setItems(data.items || []);
      setTotal(data.total_pi || 0);
    } catch (e) {
      setLoadErr('Could not reach the cart server.');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [user]);

  const removeItem = async (id) => {
    try {
      await fetch(`${API_BASE}/cart/${id}/remove/`, { method: 'DELETE', headers: authHeaders(user.token) });
      load();
    } catch (e) {}
  };

  const checkoutTravel = async (item) => {
    const m = item.metadata || {};
    const res = await fetch(`${API_BASE}/travel/bookings/create/`, {
      method: 'POST', headers: authHeaders(user.token),
      body: JSON.stringify({
        listing_id: item.reference_id, check_in: m.check_in, check_out: m.check_out,
        guests_count: m.guests_count || 1, guest_phone: m.guest_phone || '', notes: m.notes || '',
      }),
    });
    const data = await safeJson(res);
    if (!data.success) { alert(data.error || 'Could not create booking.'); return; }

    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: data.price_pi, memo: `Travel booking: ${item.title}`, metadata: { type: 'travel_booking', booking_id: data.booking_id } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/travel/bookings/${data.booking_id}/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/travel/bookings/${data.booking_id}/complete/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId, txid }),
          }).then(() => { removeItem(item.id); alert(t('ct_booking_confirmed')); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const checkoutFreelance = async (item) => {
    const m = item.metadata || {};
    const res = await fetch(`${API_BASE}/freelance/orders/create/`, {
      method: 'POST', headers: authHeaders(user.token),
      body: JSON.stringify({ package_id: item.reference_id, requirements: m.requirements || '' }),
    });
    const data = await safeJson(res);
    if (!data.success) { alert(data.error || 'Could not create order.'); return; }
    if (data.free) {
      removeItem(item.id);
      alert('Super admin - free order placed. No payment needed.');
      setCheckingOutId(null);
      return;
    }

    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    const onIncompleteFL = (payment) => {
      try {
        const pid = payment && payment.identifier;
        const md = (payment && payment.metadata) || {};
        if (md.type !== 'freelance_order' || !md.order_id) return;
        const tx = payment.transaction && payment.transaction.txid;
        if (tx) {
          fetch(`${API_BASE}/freelance/orders/${md.order_id}/complete/`, {
            method: 'POST', headers: authHeaders(user.token),
            body: JSON.stringify({ payment_id: pid, txid: tx }),
          });
        } else {
          fetch(`${API_BASE}/freelance/orders/${md.order_id}/approve/`, {
            method: 'POST', headers: authHeaders(user.token),
            body: JSON.stringify({ payment_id: pid }),
          });
        }
      } catch (e) {}
    };
    await window.Pi.authenticate(['username', 'payments'], onIncompleteFL);
    window.Pi.createPayment(
      { amount: data.price_pi, memo: `Freelance order: ${item.title}`, metadata: { type: 'freelance_order', order_id: data.order_id } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/freelance/orders/${data.order_id}/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/freelance/orders/${data.order_id}/complete/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId, txid }),
          }).then(() => { removeItem(item.id); alert(t('ct_order_placed')); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const checkoutMeetup = async (item) => {
    const res = await fetch(`${API_BASE}/meetup/events/${item.reference_id}/rsvp/`, {
      method: 'POST', headers: authHeaders(user.token),
    });
    const data = await safeJson(res);
    if (!data.success) { alert(data.error || 'Could not create RSVP.'); return; }

    if (!data.requires_payment) { removeItem(item.id); alert("You're in! (was free)"); return; }
    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: data.price_pi, memo: `Meetup RSVP: ${item.title}`, metadata: { type: 'meetup_rsvp', rsvp_id: data.rsvp_id } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/meetup/rsvps/${data.rsvp_id}/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/meetup/rsvps/${data.rsvp_id}/complete/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId, txid }),
          }).then(() => { removeItem(item.id); alert("You're in! ✓"); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const checkoutAcademy = async (item) => {
    const res = await fetch(`${API_BASE}/academy/courses/${item.reference_id}/enroll/`, {
      method: 'POST', headers: authHeaders(user.token),
    });
    const data = await safeJson(res);
    if (!data.success) { alert(data.error || 'Could not enroll.'); return; }

    if (!data.requires_payment) { removeItem(item.id); alert("You're enrolled! (was free)"); return; }
    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: data.price_pi, memo: `Enroll: ${item.title}`, metadata: { type: 'academy_enroll', enrollment_id: data.enrollment_id } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/academy/enrollments/${data.enrollment_id}/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/academy/enrollments/${data.enrollment_id}/complete/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId, txid }),
          }).then(() => { removeItem(item.id); alert("You're enrolled! ✓"); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const checkoutNFT = async (item) => {
    const res = await fetch(`${API_BASE}/nft/items/${item.reference_id}/buy/`, {
      method: 'POST', headers: authHeaders(user.token),
    });
    const data = await safeJson(res);
    if (!data.success) { alert(data.error || 'Could not start purchase.'); return; }

    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: item.price_pi, memo: `NFT: ${item.title}`, metadata: { type: 'nft_purchase', transaction_id: data.transaction_id } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/nft/purchases/${data.transaction_id}/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/nft/purchases/${data.transaction_id}/complete/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId, txid }),
          }).then(() => { removeItem(item.id); alert("It's yours! ✓"); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const checkoutMatrimonyBoost = async (item) => {
    const days = item.metadata?.days || 7;
    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: item.price_pi, memo: 'Matrimony Profile Boost', metadata: { type: 'matrimony_boost' } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/matrimony/boost/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/matrimony/boost/complete/`, {
            method: 'POST', headers: authHeaders(user.token),
            body: JSON.stringify({ payment_id: paymentId, txid, amount: item.price_pi, days }),
          }).then(() => { removeItem(item.id); alert(t('ct_boosted')); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const checkoutPetCarePremium = async (item) => {
    const days = item.metadata?.days || 365;
    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: item.price_pi, memo: `${item.title} - Premium`, metadata: { type: 'petcare_premium' } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/petcare/pets/${item.reference_id}/premium/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ payment_id: paymentId }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/petcare/pets/${item.reference_id}/premium/complete/`, {
            method: 'POST', headers: authHeaders(user.token),
            body: JSON.stringify({ payment_id: paymentId, txid, amount: item.price_pi, days }),
          }).then(() => { removeItem(item.id); alert(t('ct_premium')); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const checkoutRealty = async (item) => {
    if (!window.Pi) { alert(t('ct_pi_browser')); return; }
    await window.Pi.authenticate(['username', 'payments'], () => {});
    window.Pi.createPayment(
      { amount: item.price_pi, memo: item.title, metadata: { type: 'realty_boost' } },
      {
        onReadyForServerApproval: (paymentId) => {
          fetch(`${API_BASE}/realty/pi/approve/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ paymentId, amount: item.price_pi }),
          });
        },
        onReadyForServerCompletion: (paymentId, txid) => {
          fetch(`${API_BASE}/realty/pi/complete/`, {
            method: 'POST', headers: authHeaders(user.token), body: JSON.stringify({ paymentId, txid }),
          }).then(() => { removeItem(item.id); alert(t('ct_done')); setCheckingOutId(null); });
        },
        onCancel: () => setCheckingOutId(null),
        onError: () => { alert(t('ct_payment_error')); setCheckingOutId(null); },
      }
    );
  };

  const handleCheckout = (item) => {
    setCheckingOutId(item.id);
    if (item.app_name === 'travel') checkoutTravel(item);
    else if (item.app_name === 'freelance') checkoutFreelance(item);
    else if (item.app_name === 'meetup') checkoutMeetup(item);
    else if (item.app_name === 'academy') checkoutAcademy(item);
    else if (item.app_name === 'nft') checkoutNFT(item);
    else if (item.app_name === 'matrimony') checkoutMatrimonyBoost(item);
    else if (item.app_name === 'petcare') checkoutPetCarePremium(item);
    else if (item.app_name === 'realty') checkoutRealty(item);
    else { alert(t('ct_unsupported')); setCheckingOutId(null); }
  };

  if (!user) return <div style={{ padding: 20, textAlign: 'center' }}><h2>🔑 Login Required</h2></div>;
  if (loading) return <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>{t('ct_loading')}</div>;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: '1.5rem', fontFamily: 'sans-serif' }}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>🛒 My Cart</h1>
      <p style={{ fontSize: 13, color: '#5F5E5A', marginBottom: 16 }}>
        Marketplace has its own cart (accessible from the Marketplace app). This cart is for Travel bookings and Freelance orders. Each item pays separately - Pi payments only work one at a time.
      </p>

      {loadErr ? (
        <p style={{ textAlign: 'center', color: '#C0392B', padding: '2rem 0', fontSize: 13 }}>{loadErr}</p>
      ) : items.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999', padding: '3rem 0' }}>{t('ct_empty')}</p>
      ) : (
        <>
          <div style={{ background: '#E1F5EE', borderRadius: 12, padding: '1rem', marginBottom: 16, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: '#5F5E5A', margin: '0 0 4px' }}>{t('ct_total')}</p>
            <p style={{ fontSize: 22, fontWeight: 700, color: '#0F6E56', margin: 0 }}><PiPrice pi={total} /></p>
          </div>

          {items.map((item) => (
            <div key={item.id} style={{ background: '#fff', border: '1px solid #E3E1D9', borderRadius: 10, padding: '0.85rem 1rem', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, color: '#5F5E5A', margin: '0 0 2px', textTransform: 'uppercase', fontWeight: 600 }}>{item.app_name}</p>
                <p style={{ fontSize: 14, fontWeight: 500, margin: '0 0 2px' }}>{item.title}</p>
                <p style={{ fontSize: 13, fontWeight: 600, color: '#0F6E56', margin: 0 }}><PiPrice pi={item.line_total_pi} suffix={item.quantity > 1 ? ` (${item.quantity} x ${item.price_pi})` : ''} /></p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button
                  onClick={() => handleCheckout(item)}
                  disabled={checkingOutId === item.id}
                  style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none', borderRadius: 8, background: '#0F6E56', color: '#fff', cursor: 'pointer' }}
                >
                  {checkingOutId === item.id ? 'Processing...' : 'Checkout'}
                </button>
                <button
                  onClick={() => removeItem(item.id)}
                  style={{ padding: '6px 14px', fontSize: 12, border: '1px solid #E3E1D9', borderRadius: 8, background: '#fff', color: '#5F5E5A', cursor: 'pointer' }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
